import { type KeyboardEvent, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from "@akuna/ui";
import { Checkbox } from "@akuna/ui";
import { Input } from "@akuna/ui";
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Trash2, DollarSign, CreditCard, Banknote, QrCode, Clock } from 'lucide-react';
import { Payment, PaymentMethod, type DiscountType } from '@/hooks/useSales';
import { normalizeCurrencyInput, parseCurrencyInput } from '@/lib/utils';
import { toast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';

interface PaymentSectionProps {
  payments: Payment[];
  total: number;
  totalPaid: number;
  change: number;
  onAddPayment: (payment: Omit<Payment, 'id'>) => void;
  onRemovePayment: (id: string) => void;
  discountType?: DiscountType;
  discountValue?: number;
  onDiscountTypeChange?: (type: DiscountType) => void;
  onDiscountValueChange?: (value: number) => void;
  subtotal?: number;
  discountAmount?: number;
  variant?: 'card' | 'embedded';
}

const paymentMethods: Array<{ value: PaymentMethod; label: string }> = [
  { value: 'CARTAO_CREDITO', label: 'creditCard' },
  { value: 'CARTAO_DEBITO', label: 'debitCard' },
  { value: 'DINHEIRO', label: 'cash' },
  { value: 'PIX', label: 'pix' },
  { value: 'A_RECEBER', label: 'receivable' },
];

const paymentMethodActionKey: Record<PaymentMethod, string> = {
  CARTAO_CREDITO: 'paymentSection.methodActions.creditCard',
  CARTAO_DEBITO: 'paymentSection.methodActions.debitCard',
  DINHEIRO: 'paymentSection.methodActions.cash',
  PIX: 'paymentSection.methodActions.pix',
  A_RECEBER: 'paymentSection.methodActions.receivable',
};

/**
 * Internal state: `normalizeCurrencyInput` shape (dot decimal, no thousands).
 * Display: pt-BR — milhar com `.`, centavos com `,`.
 */
const currencyNormalizedToPtBrDisplay = (normalized: string): string => {
  if (normalized === '') return '';
  const dotIdx = normalized.indexOf('.');
  const intRaw = dotIdx === -1 ? normalized : normalized.slice(0, dotIdx);
  const decRaw = dotIdx === -1 ? '' : normalized.slice(dotIdx + 1);
  const intDigits = intRaw.replace(/\D/g, '') || '0';
  const intWithSep = intDigits.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  const decTwo = decRaw.slice(0, 2).padEnd(2, '0');
  return `${intWithSep},${decTwo}`;
};

const formatCentsToPtBr = (cents: number): string =>
  (cents / 100).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const getMethodIcon = (method: PaymentMethod, className?: string) => {
  const props = { className: className || 'h-5 w-5' };

  switch (method) {
    case 'DINHEIRO':
      return <Banknote {...props} />;
    case 'CARTAO_DEBITO':
      return <CreditCard {...props} />;
    case 'CARTAO_CREDITO':
      return <CreditCard {...props} />;
    case 'PIX':
      return <QrCode {...props} />;
    case 'A_RECEBER':
      return <Clock {...props} />;
    default:
      return <DollarSign {...props} />;
  }
};

export const PaymentSection = ({
  payments,
  total,
  totalPaid,
  change,
  onAddPayment,
  onRemovePayment,
  discountType,
  discountValue,
  onDiscountTypeChange,
  onDiscountValueChange,
  subtotal,
  discountAmount,
  variant = 'card',
}: PaymentSectionProps) => {
  const { t } = useTranslation("erp");
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('CARTAO_CREDITO');
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentAmountDisplay, setPaymentAmountDisplay] = useState('');
  const [isEditingPaymentAmount, setIsEditingPaymentAmount] = useState(false);
  const [discountDisplay, setDiscountDisplay] = useState('');
  const [isSplitMode, setIsSplitMode] = useState(false);
  const [showDiscountForm, setShowDiscountForm] = useState(
    (discountAmount ?? 0) > 0 || (discountValue ?? 0) > 0,
  );
  /** Last "remaining to pay" that the amount field was aligned to (auto or split); used to bump the field when the cart total changes without the user typing a custom value. */
  const prevRemainingSyncedRef = useRef(0);
  const paymentAmountInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (discountType === 'fixed' && discountValue != null && discountValue > 0 && discountDisplay === '') {
      setDiscountDisplay(normalizeCurrencyInput(String(discountValue)));
    }
  }, [discountType, discountValue, discountDisplay]);

  useEffect(() => {
    if (!isEditingPaymentAmount) {
      setPaymentAmountDisplay(paymentAmount === '' ? '' : currencyNormalizedToPtBrDisplay(paymentAmount));
    }
  }, [paymentAmount, isEditingPaymentAmount]);

  useLayoutEffect(() => {
    const remainingAmount = Math.max(0, total - totalPaid);
    const prevRemaining = prevRemainingSyncedRef.current;

    if (remainingAmount === 0) {
      if (paymentAmount !== '') {
        setPaymentAmount('');
      }
      if (isSplitMode) {
        setIsSplitMode(false);
      }
      prevRemainingSyncedRef.current = 0;
      return;
    }

    if (isEditingPaymentAmount) {
      prevRemainingSyncedRef.current = remainingAmount;
      return;
    }

    const parsedAmount = parseCurrencyInput(paymentAmount);
    const stillMatchesSyncedRemaining =
      paymentAmount === '' || Math.abs(parsedAmount - prevRemaining) < 0.01;

    if (stillMatchesSyncedRemaining && Math.abs(parsedAmount - remainingAmount) >= 0.005) {
      setPaymentAmount(normalizeCurrencyInput(String(remainingAmount)));
    }

    prevRemainingSyncedRef.current = remainingAmount;
  }, [total, totalPaid, paymentAmount, isSplitMode, isEditingPaymentAmount]);

  useEffect(() => {
    if ((discountAmount ?? 0) > 0 || (discountValue ?? 0) > 0) {
      setShowDiscountForm(true);
    }
  }, [discountAmount, discountValue]);

  const formatBRL = (value: number) =>
    new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);

  const paymentAmountCents = Math.round(parseCurrencyInput(paymentAmount) * 100);

  const applyPaymentAmountCents = (nextCents: number) => {
    const clampedCents = Math.max(0, Math.min(nextCents, 999999999));
    const normalized = clampedCents === 0 ? '' : normalizeCurrencyInput(String(clampedCents / 100));
    setPaymentAmount(normalized);
    setPaymentAmountDisplay(clampedCents === 0 ? '' : formatCentsToPtBr(clampedCents));
  };

  const setPaymentCaretFromDigitsRight = (digitsRight: number) => {
    requestAnimationFrame(() => {
      const input = paymentAmountInputRef.current;
      if (!input) return;

      const after = input.value;
      if (digitsRight <= 0) {
        input.setSelectionRange(after.length, after.length);
        return;
      }

      let counted = 0;
      let newPos = after.length;
      for (let index = after.length - 1; index >= 0; index -= 1) {
        if (/\d/.test(after[index])) counted += 1;
        if (counted === digitsRight) {
          newPos = index;
          break;
        }
      }
      input.setSelectionRange(newPos, newPos);
    });
  };

  const getPaymentDigitsSelection = (value: string, selectionStart: number, selectionEnd: number) => {
    const digitsBefore = value.slice(0, selectionStart).replace(/\D/g, '').length;
    const digitsSelected = value.slice(selectionStart, selectionEnd).replace(/\D/g, '').length;
    const digitsAfter = value.slice(selectionEnd).replace(/\D/g, '').length;

    return { digitsBefore, digitsSelected, digitsAfter };
  };

  const handlePaymentAmountKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    const input = event.currentTarget;
    const value = input.value;
    const selectionStart = input.selectionStart ?? value.length;
    const selectionEnd = input.selectionEnd ?? value.length;
    const hasSelection = selectionEnd > selectionStart;

    if (event.ctrlKey || event.metaKey || event.altKey) return;

    if (
      event.key === 'Tab' ||
      event.key.startsWith('Arrow') ||
      event.key === 'Home' ||
      event.key === 'End' ||
      event.key === 'Enter' ||
      event.key === 'Escape'
    ) {
      return;
    }

    if (event.key === 'Backspace') {
      event.preventDefault();

      if (hasSelection) {
        const { digitsBefore, digitsSelected, digitsAfter } = getPaymentDigitsSelection(
          value,
          selectionStart,
          selectionEnd,
        );

        if (digitsSelected === 0) return;

        const centsStr = String(paymentAmountCents).padStart(3, '0');
        const startIdx = digitsBefore;
        const endIdx = centsStr.length - digitsAfter;
        const nextStr = centsStr.slice(0, startIdx) + centsStr.slice(endIdx);
        applyPaymentAmountCents(parseInt(nextStr || '0', 10));
        setPaymentCaretFromDigitsRight(digitsAfter);
        return;
      }

      let index = selectionStart - 1;
      while (index >= 0 && !/\d/.test(value[index])) index -= 1;
      if (index < 0) return;

      const digitsAfter = value.slice(index + 1).replace(/\D/g, '').length;
      const centsStr = String(paymentAmountCents).padStart(3, '0');
      const digitIdx = centsStr.length - 1 - digitsAfter;
      const nextStr = centsStr.slice(0, digitIdx) + centsStr.slice(digitIdx + 1);
      applyPaymentAmountCents(parseInt(nextStr || '0', 10));
      setPaymentCaretFromDigitsRight(digitsAfter);
      return;
    }

    if (event.key === 'Delete') {
      event.preventDefault();

      if (hasSelection) {
        const { digitsBefore, digitsSelected, digitsAfter } = getPaymentDigitsSelection(
          value,
          selectionStart,
          selectionEnd,
        );

        if (digitsSelected === 0) return;

        const centsStr = String(paymentAmountCents).padStart(3, '0');
        const startIdx = digitsBefore;
        const endIdx = centsStr.length - digitsAfter;
        const nextStr = centsStr.slice(0, startIdx) + centsStr.slice(endIdx);
        applyPaymentAmountCents(parseInt(nextStr || '0', 10));
        setPaymentCaretFromDigitsRight(digitsAfter);
        return;
      }

      let index = selectionStart;
      while (index < value.length && !/\d/.test(value[index])) index += 1;
      if (index >= value.length) return;

      const digitsAfter = value.slice(index + 1).replace(/\D/g, '').length;
      const centsStr = String(paymentAmountCents).padStart(3, '0');
      const digitIdx = centsStr.length - 1 - digitsAfter;
      const nextStr = centsStr.slice(0, digitIdx) + centsStr.slice(digitIdx + 1);
      applyPaymentAmountCents(parseInt(nextStr || '0', 10));
      setPaymentCaretFromDigitsRight(digitsAfter);
      return;
    }

    if (event.key.length === 1 && /\d/.test(event.key)) {
      event.preventDefault();
      const centsStr = String(paymentAmountCents).padStart(3, '0');
      const { digitsBefore, digitsSelected, digitsAfter } = getPaymentDigitsSelection(
        value,
        selectionStart,
        selectionEnd,
      );
      const insertAt = digitsBefore;
      const replaceEnd = centsStr.length - digitsAfter;
      const nextStr = `${centsStr.slice(0, insertAt)}${event.key}${centsStr.slice(replaceEnd)}`;
      applyPaymentAmountCents(parseInt(nextStr, 10));
      setPaymentCaretFromDigitsRight(digitsAfter);
      return;
    }

    if (event.key.length === 1) {
      event.preventDefault();
    }
  };

  const handleAddPayment = () => {
    const amount = parseCurrencyInput(paymentAmount);
    const remainingAmount = Math.max(0, total - totalPaid);
    const allowsChange = paymentMethod === 'DINHEIRO';

    if (amount <= 0) {
      return;
    }

    if (!allowsChange && amount > remainingAmount) {
      toast({
        title: t('paymentSection.invalidValueTitle'),
        description: t('paymentSection.invalidValueDescription', { amount: formatBRL(remainingAmount) }),
        variant: 'destructive',
      });
      return;
    }

    onAddPayment({ method: paymentMethod, amount });

    const nextRemaining = Math.max(0, remainingAmount - amount);

    if (nextRemaining === 0 || amount > remainingAmount) {
      setPaymentAmount('');
      setIsSplitMode(false);
      return;
    }

    setIsSplitMode(true);
    setPaymentAmount(normalizeCurrencyInput(String(nextRemaining)));
  };

  const remaining = total - totalPaid;
  const paymentAmountValue = parseCurrencyInput(paymentAmount);
  const remainingAfterPayment = Math.max(0, remaining - paymentAmountValue);
  const remainingCents = Math.round(Math.max(0, remaining) * 100);
  const showUseRemainingLink = remainingCents > 0 && paymentAmountCents !== remainingCents;
  const selectedMethod = paymentMethods.find((method) => method.value === paymentMethod);

  const hasDiscountControls =
    discountType !== undefined &&
    discountValue !== undefined &&
    onDiscountTypeChange &&
    onDiscountValueChange &&
    subtotal !== undefined &&
    discountAmount !== undefined;

  const discountMaxFixed = subtotal ?? 0;

  const discountContent = hasDiscountControls ? (
    <div className="space-y-3 rounded-lg border p-4">
      <div className="flex items-center justify-between gap-3">
        <div className="space-y-1">
          <Label className="text-sm font-medium">{t('paymentSection.applyDiscount')}</Label>
        </div>

        <Button
          type="button"
          size="sm"
          variant={showDiscountForm ? 'secondary' : 'outline'}
          onClick={() => {
            if (showDiscountForm) {
              onDiscountValueChange(0);
              setDiscountDisplay('');
            }
            setShowDiscountForm((current) => !current);
          }}
        >
          {showDiscountForm ? t('paymentSection.removeDiscount') : t('paymentSection.addDiscount')}
        </Button>
      </div>

      {showDiscountForm && (
        <div className="space-y-3 border-t pt-3">
          <div className="space-y-2">
            <Label>{t('paymentSection.discountType')}</Label>
            <Select
              value={discountType}
              onValueChange={(value: DiscountType) => {
                onDiscountTypeChange(value);
                onDiscountValueChange(0);
                setDiscountDisplay('');
              }}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="percent">{t('paymentSection.discountPercent')}</SelectItem>
                <SelectItem value="fixed">{t('paymentSection.discountFixed')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>{discountType === 'percent' ? t('paymentSection.discountPercentLabel') : t('paymentSection.discountValueLabel')}</Label>
            <Input
              type="text"
              inputMode="decimal"
              value={discountType === 'percent' ? String(discountValue ?? 0) : discountDisplay}
              onChange={(e) => {
                if (discountType === 'percent') {
                  const raw = parseFloat(e.target.value.replace(',', '.'));
                  const num = Number.isNaN(raw) ? 0 : raw;
                  onDiscountValueChange(Math.min(100, Math.max(0, num)));
                  return;
                }

                const normalized = normalizeCurrencyInput(e.target.value);
                setDiscountDisplay(normalized);
                const parsed = parseCurrencyInput(normalized);
                onDiscountValueChange(Math.min(discountMaxFixed, Math.max(0, parsed)));
              }}
              placeholder={discountType === 'percent' ? '0' : '0,00'}
            />
          </div>
        </div>
      )}
    </div>
  ) : null;

  const content = (
    <div className="space-y-5">
      {remaining > 0 && (
        <div className="space-y-4 rounded-lg border p-4">
          <div className="space-y-2">
            <Label>{t('paymentSection.paymentMethod')}</Label>
            <Select value={paymentMethod} onValueChange={(value: PaymentMethod) => setPaymentMethod(value)}>
              <SelectTrigger>
                <SelectValue placeholder={t('paymentSection.paymentMethodPlaceholder')} />
              </SelectTrigger>
              <SelectContent>
                {paymentMethods.map((method) => (
                  <SelectItem key={method.value} value={method.value}>
                    {t(`paymentSection.methods.${method.label}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>{t('paymentSection.amountToPay')}</Label>
              {showUseRemainingLink && (
                <Button
                  type="button"
                  variant="link"
                  className="h-auto p-0 text-sm text-muted-foreground"
                  onClick={() => setPaymentAmount(normalizeCurrencyInput(String(Math.max(0, remaining))))}
                >
                  {t('paymentSection.useRemaining')}
                </Button>
              )}
            </div>

            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">R$</span>
              <Input
                ref={paymentAmountInputRef}
                type="text"
                inputMode="decimal"
                style={{ paddingLeft: "2.5rem" }}
                placeholder="0,00"
                value={paymentAmountDisplay}
                onFocus={() => {
                  setIsEditingPaymentAmount(true);
                  if (paymentAmountCents === 0) {
                    setPaymentAmountDisplay('');
                  }
                }}
                onKeyDown={handlePaymentAmountKeyDown}
                onChange={() => {}}
                onBlur={() => {
                  setIsEditingPaymentAmount(false);
                  setPaymentAmountDisplay(paymentAmountCents > 0 ? formatCentsToPtBr(paymentAmountCents) : '');
                }}
              />
            </div>

            {paymentMethod === 'DINHEIRO' ? (
              <p className="text-xs text-muted-foreground">
                {`${t('paymentSection.methods.cash')}: ${t('paymentSection.change')}`}
              </p>
            ) : (
              remainingAfterPayment > 0 && (
                <p className="text-xs text-muted-foreground">
                  {`${t('paymentSection.remaining')}: ${formatBRL(remainingAfterPayment)}`}
                </p>
              )
            )}
          </div>

          <div className="flex items-center gap-2">
            <Checkbox
              id="split-payment"
              checked={isSplitMode}
              onCheckedChange={(checked) => {
                const nextValue = checked === true;
                setIsSplitMode(nextValue);

                if (!nextValue) {
                  setPaymentAmount(normalizeCurrencyInput(String(Math.max(0, remaining))));
                }
              }}
            />
            <Label htmlFor="split-payment" className="text-sm !font-normal">
              {t('paymentSection.splitPayment')}
            </Label>
          </div>

          <Button type="button" className="w-full" onClick={handleAddPayment}>
            {isSplitMode
              ? t("common.add")
              : t(
                selectedMethod
                  ? paymentMethodActionKey[selectedMethod.value]
                  : "paymentSection.methodActions.default",
              )}
          </Button>
        </div>
      )}


      {discountContent}

      {payments.length > 0 && (
        <div className="space-y-3">
          <Label className="text-base">{t('paymentSection.addedPayments')}</Label>
          <div className="space-y-2">
            {payments.map((payment) => (
              <div key={payment.id} className="flex items-center justify-between rounded-lg border p-3">
                <div className="flex items-center gap-3">
                  <div className="rounded-full bg-primary/10 p-2 text-primary">
                    {getMethodIcon(payment.method, 'h-4 w-4')}
                  </div>
                  <div>
                    <p className="font-medium">{t(`paymentSection.methods.${paymentMethods.find((method) => method.value === payment.method)?.label}`)}</p>
                    <p className="text-sm text-muted-foreground">{formatBRL(payment.amount)}</p>
                  </div>
                </div>

                <Button
                  size="icon"
                  variant="ghost"
                  className="h-9 w-9 text-muted-foreground hover:text-destructive"
                  onClick={() => onRemovePayment(payment.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-3 rounded-lg border bg-muted/20 p-4">
        <div className="flex items-end justify-between">
          <p className="text-sm text-muted-foreground">{t('paymentSection.totalToPay')}</p>
          <p className="text-2xl font-bold">{formatBRL(total)}</p>
        </div>

        <div className="space-y-2 border-t pt-3 text-sm">
          {(discountAmount ?? 0) > 0 && (
            <div className="flex items-center justify-between text-green-600">
              <span>{t('paymentSection.discountApplied')}</span>
              <span>- {formatBRL(discountAmount ?? 0)}</span>
            </div>
          )}

          {payments.length > 0 && (
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">{t('paymentSection.paid')}</span>
              <span className="font-medium">{formatBRL(totalPaid)}</span>
            </div>
          )}

          {remaining > 0 && (
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">{t('paymentSection.remaining')}</span>
              <span className="font-semibold text-foreground">{formatBRL(Math.max(0, remaining))}</span>
            </div>
          )}

          {change > 0 && (
            <div className="flex items-center justify-between text-green-600">
              <span>{t('paymentSection.change')}</span>
              <span className="font-semibold">{formatBRL(change)}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  if (variant === 'embedded') {
    return (
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">{t('paymentSection.title')}</h3>
        {content}
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('paymentSection.title')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">{content}</CardContent>
    </Card>
  );
};
